import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircle, Send, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import { interactionsService } from '@/api/services/interactions';
import { useAuth } from '@/providers/AuthProvider';
import type { Comment } from '@/types';

interface CommentsSectionProps {
  contentId: string;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes}min`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
}

function CommentItem({
  comment,
  currentUserId,
  onDelete,
  isDeleting,
}: {
  comment: Comment;
  currentUserId?: string;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  const isOwn = comment.userId === currentUserId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="flex gap-3 py-3"
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F4F2F2] flex items-center justify-center">
        <User className="w-4 h-4 text-[#888185]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-[#2E2A23]">
            {comment.userName || 'Usuário'}
          </span>
          <span className="text-xs text-[#888185]">{formatDate(comment.createdAt)}</span>
        </div>
        <p className="text-sm text-[#888185] break-words">{comment.content}</p>
      </div>
      {isOwn && (
        <button
          type="button"
          onClick={() => onDelete(comment.id)}
          disabled={isDeleting}
          className="flex-shrink-0 p-1.5 text-[#888185] hover:text-[#E94E1B] transition-colors disabled:opacity-50"
          aria-label="Excluir comentário"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </motion.div>
  );
}

export function CommentsSection({ contentId }: CommentsSectionProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    data: comments = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['comments', contentId],
    queryFn: () => interactionsService.getComments(contentId),
    enabled: isExpanded,
  });

  const createMutation = useMutation({
    mutationFn: (content: string) => interactionsService.createComment(contentId, content),
    onSuccess: (newComment) => {
      queryClient.setQueryData<Comment[]>(['comments', contentId], (old = []) => [
        ...old,
        { ...newComment, userName: user?.user_metadata?.name || 'Você' },
      ]);
      setNewComment('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => interactionsService.deleteComment(commentId),
    onSuccess: (_, commentId) => {
      queryClient.setQueryData<Comment[]>(['comments', contentId], (old = []) =>
        old.filter((c) => c.id !== commentId),
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim() && !createMutation.isPending) {
      createMutation.mutate(newComment.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (newComment.trim() && !createMutation.isPending) {
        createMutation.mutate(newComment.trim());
      }
    }
  };

  const handleDelete = (commentId: string) => {
    if (!deleteMutation.isPending) {
      deleteMutation.mutate(commentId);
    }
  };

  return (
    <div className="border-t border-[#CAC7C7] mt-12 pt-8">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-[#888185] hover:text-[#2E2A23] transition-colors mb-4"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-medium">
          {isExpanded ? 'Ocultar comentários' : 'Mostrar comentários'}
          {comments.length > 0 && ` (${comments.length})`}
        </span>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            {/* Comment input */}
            <form onSubmit={handleSubmit} className="mb-6">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E94E1B] flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="relative">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value.slice(0, 200))}
                      onKeyDown={handleKeyDown}
                      placeholder="Adicione um comentário..."
                      rows={2}
                      maxLength={200}
                      className="w-full px-3 py-2 pr-10 text-sm border border-[#CAC7C7] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#E94E1B]/20 focus:border-[#E94E1B] bg-white"
                    />
                    <button
                      type="submit"
                      disabled={!newComment.trim() || createMutation.isPending}
                      className="absolute right-2 bottom-2 p-1.5 text-[#E94E1B] hover:bg-[#FDEAE3] rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Enviar comentário"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                  {newComment.length > 0 && (
                    <div
                      className={`text-xs mt-1 text-right ${newComment.length >= 180 ? 'text-[#E94E1B]' : 'text-[#888185]'}`}
                    >
                      {newComment.length}/200
                    </div>
                  )}
                </div>
              </div>
            </form>

            {/* Comments list */}
            {isLoading ? (
              <div className="py-4 text-center text-sm text-[#888185]">Carregando...</div>
            ) : error ? (
              <div className="py-4 text-center text-sm text-[#E94E1B]">
                Erro ao carregar comentários
              </div>
            ) : comments.length === 0 ? (
              <div className="py-4 text-center text-sm text-[#888185]">
                Nenhum comentário ainda. Seja o primeiro!
              </div>
            ) : (
              <div className="divide-y divide-[#CAC7C7]">
                <AnimatePresence>
                  {comments.map((comment) => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      currentUserId={user?.id}
                      onDelete={handleDelete}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
